import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './common.js';

export const FormaCusteioSchema = z.enum(['adiantamento', 'cartao_empresa', 'reembolso']);

/** Meio de locomoção da viagem — habilita o controle de odômetro/km quando `carro` (RF-VIA). */
export const TipoLocomocaoSchema = z.enum(['carro', 'aviao', 'onibus', 'outro']);
export type TipoLocomocao = z.infer<typeof TipoLocomocaoSchema>;

export const TripStatusSchema = z.enum([
  'rascunho',
  'aguardando_aprovacao',
  'aprovada',
  'em_andamento',
  'finalizada',
  'em_prestacao',
  'encerrada',
  'rejeitada',
]);

export const CreateTripSchema = z.object({
  origem: z.string().min(1),
  destino: z.string().min(1),
  dias: z.number().int().positive(),
  estimativaItens: z.array(z.object({ nome: z.string().min(1), valor: MoneyStringSchema })).min(1),
  formaCusteio: FormaCusteioSchema.default('adiantamento'),
  tipoLocomocao: TipoLocomocaoSchema.default('carro'),
  /** Odômetro na saída (km). Só se aplica a `carro`; permite calcular o km rodado na viagem. */
  odometroInicio: z.number().int().nonnegative().optional(),
  /** Moeda da viagem. Se ausente, herda do orçamento vinculado ou da moeda base do tenant. */
  moeda: CurrencySchema.optional(),
  viajanteId: z.string().uuid().optional(),
  clientId: z.string().uuid().optional(),
  quoteId: z.string().uuid().optional(),
});
export type CreateTripDTO = z.infer<typeof CreateTripSchema>;

export const AddExpenseSchema = z.object({
  descricao: z.string().min(1),
  valor: MoneyStringSchema,
  fotoUrl: z.string().url().optional(),
  /** Chave gerada no dispositivo — dedup offline (RNF-14, DoD 12). */
  idempotencyKey: z.string().min(1),
});
export type AddExpenseDTO = z.infer<typeof AddExpenseSchema>;

/** Ao finalizar a viagem, registra o odômetro de chegada para fechar o km rodado. */
export const FinalizeTripSchema = z.preprocess(
  (v) => v ?? {},
  z.object({
    odometroFim: z.number().int().nonnegative().optional(),
  }),
);
export type FinalizeTripDTO = z.infer<typeof FinalizeTripSchema>;

export const TripViewSchema = z.object({
  id: z.string().uuid(),
  origem: z.string(),
  destino: z.string(),
  dias: z.number(),
  estimativa: MoneyStringSchema,
  teto: MoneyStringSchema,
  status: TripStatusSchema,
  formaCusteio: FormaCusteioSchema,
  moeda: CurrencySchema,
  tipoLocomocao: TipoLocomocaoSchema,
  odometroInicio: z.number().int().nullable(),
  odometroFim: z.number().int().nullable(),
  /** Km rodado na viagem (odometroFim − odometroInicio) quando ambos existem. */
  kmRodado: z.number().int().nullable(),
  totalGasto: MoneyStringSchema,
  sobra: MoneyStringSchema,
  clientId: z.string().uuid().nullable(),
  quoteId: z.string().uuid().nullable(),
  /** Número do orçamento vinculado (RF-VIA-10) — a viagem entra numa caixinha do orçamento. */
  quoteNumero: z.number().int().nullable(),
  expenses: z.array(
    z.object({
      id: z.string().uuid(),
      descricao: z.string(),
      valor: MoneyStringSchema,
      fotoUrl: z.string().nullable(),
    }),
  ),
});
export type TripViewDTO = z.infer<typeof TripViewSchema>;
